import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../App';
import { Award, TrendingUp, Zap, Target, MessageCircle, Send, User, ChevronRight, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import SocialLoginButtons from '../components/SocialLoginButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QBClubPage = () => {
  const { user } = useContext(AuthContext);
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clubRes, commentsRes] = await Promise.all([
          axios.get(`${API}/qb/club`),
          axios.get(`${API}/comments/qb-club-2026`)
        ]);
        setClubData(clubRes.data);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load QB profiles');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${API}/comments`,
        { article_id: 'qb-club-2026', content: newComment },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not yet synced';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Helmet>
        <title>2026 SWAC QB Club | SheLovesThee</title>
        <meta name="description" content="Track the top SWAC quarterbacks of the 2026 season. Week-by-week stats, rankings, and analysis." />
      </Helmet>
      
      <div className="min-h-screen bg-slate-50" data-testid="qb-club-page">
        {/* Header */}
        <div className="bg-[#001f3f] text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4 mb-4">
              <Award className="h-12 w-12 text-[#DC2626]" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase heading-font" data-testid="qb-club-title">
                2026 SWAC QB CLUB
              </h1>
            </div>
            <p className="text-xl text-slate-300">
              Tracking the SWAC's quarterbacks week by week.
            </p>
            {clubData && (
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-400">
                <span>Season: {clubData.season}</span>
                <span>•</span>
                <span>Current Week: {clubData.current_week}</span>
                <span>•</span>
                <span>Last Updated: {formatDate(clubData.last_updated)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="text-center py-12" data-testid="loading">Loading QB profiles...</div>
          ) : !clubData ? (
            <div className="text-center py-12 text-slate-600" data-testid="no-qbs">
              QB profiles coming soon! Check back later.
            </div>
          ) : (
            <>
              {/* Active QBs Section */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-[#001f3f] uppercase tracking-wider mb-6 flex items-center">
                  <TrendingUp className="h-6 w-6 mr-2 text-green-500" />
                  Active Quarterbacks ({clubData.active_qbs?.length || 0})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {clubData.active_qbs?.map(qb => (
                    <QBCard key={qb.player_id} qb={qb} currentWeek={clubData.current_week} />
                  ))}
                </div>
              </div>

              {/* Injured QBs Section */}
              {clubData.injured_qbs?.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-2xl font-bold text-[#001f3f] uppercase tracking-wider mb-6 flex items-center">
                    <AlertTriangle className="h-6 w-6 mr-2 text-yellow-500" />
                    QB Club — Injury / Availability
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {clubData.injured_qbs.map(qb => (
                      <InjuredQBCard key={qb.player_id} qb={qb} />
                    ))}
                  </div>
                </div>
              )}

              {/* Data Source Attribution */}
              <div className="bg-slate-100 rounded-lg p-4 mb-16 text-center text-sm text-slate-600">
                <p><strong>Statistics Source:</strong> Southwestern Athletic Conference (SWAC)</p>
                <p className="mt-1">
                  <strong>Current Week:</strong> Week {clubData.current_week} | 
                  <strong className="ml-2">Last Updated:</strong> {formatDate(clubData.last_updated)}
                </p>
              </div>

              {/* Comment Section */}
              <div className="border-t-4 border-[#DC2626] pt-12" data-testid="qb-club-comments">
                <h2 className="text-3xl font-bold uppercase heading-font text-[#001f3f] mb-8 flex items-center">
                  <MessageCircle className="h-8 w-8 mr-3 text-[#DC2626]" />
                  Fan Comments
                </h2>

                {/* Comment Form */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                  {user ? (
                    <form onSubmit={handleSubmitComment} className="space-y-4">
                      <div className="flex items-start space-x-4">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#001f3f] flex items-center justify-center">
                            <User className="h-6 w-6 text-white" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-bold text-[#001f3f] mb-2">{user.name}</p>
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Share your thoughts on the QB Club race..."
                            className="w-full p-4 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent resize-none"
                            rows="3"
                            data-testid="comment-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={submittingComment || !newComment.trim()}
                          className="flex items-center gap-2 bg-[#DC2626] hover:bg-[#b91c1c] text-white px-6 py-3 rounded-full font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          data-testid="submit-comment-btn"
                        >
                          <Send className="h-5 w-5" />
                          {submittingComment ? 'Posting...' : 'Post Comment'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-slate-600 mb-6 text-lg">Login to join the conversation!</p>
                      <SocialLoginButtons />
                    </div>
                  )}
                </div>

                {/* Comments List */}
                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No comments yet. Be the first to share your thoughts!
                    </div>
                  ) : (
                    comments.map(comment => (
                      <CommentCard key={comment.comment_id} comment={comment} />
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const QBCard = ({ qb, currentWeek }) => {
  const stats = qb.season_stats;
  const schoolInfo = {
    abbr: qb.abbr || qb.school?.substring(0, 4).toUpperCase(),
    color: qb.color || '#001f3f'
  };

  return (
    <div 
      className="bg-white shadow-lg overflow-hidden group hover:shadow-xl transition-shadow rounded-xl border-l-4"
      style={{ borderLeftColor: schoolInfo.color }}
      data-testid={`qb-card-${qb.player_id}`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Image Section */}
        <div className="md:w-2/5">
          {qb.photo_url ? (
            <div className="h-64 md:h-full overflow-hidden bg-slate-200">
              <img 
                src={qb.photo_url} 
                alt={qb.name} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          ) : (
            <div 
              className="h-64 md:h-full flex items-center justify-center"
              style={{ backgroundColor: schoolInfo.color }}
            >
              <span className="text-6xl font-bold text-white/30">{qb.name?.charAt(0)}</span>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="md:w-3/5 p-6">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-2xl font-bold uppercase heading-font text-[#001f3f]" data-testid="qb-name">
                {qb.name}
              </h3>
              <p className="text-sm font-bold uppercase" style={{ color: schoolInfo.color }}>
                #{qb.number} • {qb.school}
              </p>
            </div>
            <span 
              className="text-xs font-bold px-2 py-1 rounded text-white"
              style={{ backgroundColor: schoolInfo.color }}
            >
              {schoolInfo.abbr}
            </span>
          </div>
          
          {/* 2026 Season Stats */}
          {stats ? (
            <div className="border-t border-slate-200 pt-4 mt-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-3 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                2026 Season Stats
              </h4>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <StatBox label="PASS YDS" value={stats.passing_yards?.toLocaleString() || '—'} />
                <StatBox label="PASS TD" value={stats.passing_tds || '—'} />
                <StatBox label="INT" value={stats.interceptions || '—'} />
                <StatBox label="COMP %" value={stats.completion_pct ? `${stats.completion_pct}%` : '—'} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <StatBox label="RUSH YDS" value={stats.rushing_yards?.toLocaleString() || '—'} />
                <StatBox label="RUSH TD" value={stats.rushing_tds || '—'} />
                <StatBox label="TOTAL TD" value={stats.total_tds || '—'} highlight />
              </div>
            </div>
          ) : (
            <div className="border-t border-slate-200 pt-4 mt-4 text-center text-slate-500">
              <p>Season stats pending first sync</p>
            </div>
          )}
          
          {/* View Weekly Breakdown Button */}
          <Link
            to="/qb-analytics"
            className="mt-4 flex items-center justify-center gap-2 bg-[#001f3f] hover:bg-[#003366] text-white px-4 py-3 rounded-lg font-bold uppercase tracking-wider transition-colors text-sm"
            data-testid="view-weekly-btn"
          >
            View Weekly Breakdown
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, highlight }) => (
  <div 
    className={`text-center p-2 rounded ${highlight ? 'bg-[#DC2626] text-white' : 'bg-slate-50'}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className={`text-lg font-bold ${highlight ? 'text-white' : 'text-[#007AFF]'}`}>{value}</div>
    <div className={`text-[10px] uppercase leading-tight ${highlight ? 'text-white/80' : 'text-slate-600'}`}>{label}</div>
  </div>
);

const InjuredQBCard = ({ qb }) => {
  const isOutTemporarily = qb.injury_status === 'OUT_TEMPORARILY';
  const stats = qb.season_stats;
  
  return (
    <div 
      className="bg-white shadow-md rounded-xl overflow-hidden border-2 border-yellow-400"
      data-testid={`injured-qb-${qb.player_id}`}
    >
      <div className="bg-yellow-50 px-4 py-2 flex items-center justify-between">
        <span className="font-bold text-yellow-800 text-sm uppercase">
          {isOutTemporarily ? 'OUT TEMPORARILY' : 'OUT FOR SEASON'}
        </span>
        {qb.injury_note && (
          <span className="text-xs text-yellow-700">{qb.injury_note}</span>
        )}
      </div>
      
      <div className="p-4 flex items-center gap-4">
        {qb.photo_url ? (
          <img 
            src={qb.photo_url} 
            alt={qb.name} 
            className="w-20 h-20 rounded-full object-cover grayscale"
          />
        ) : (
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: qb.color || '#666' }}
          >
            <span className="text-2xl font-bold text-white/50">{qb.name?.charAt(0)}</span>
          </div>
        )}
        
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#001f3f]">{qb.name}</h3>
          <p className="text-sm text-slate-600">{qb.school}</p>
          
          {/* Show preserved 2026 stats */}
          {stats && (
            <div className="mt-2 flex gap-4 text-xs">
              <span className="text-slate-500">
                <strong>{stats.passing_yards || 0}</strong> Pass Yds
              </span>
              <span className="text-slate-500">
                <strong>{stats.passing_tds || 0}</strong> TD
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentCard = ({ comment }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4 flex space-x-4" data-testid={`comment-${comment.comment_id}`}>
      {comment.user_picture ? (
        <img src={comment.user_picture} alt={comment.user_name} className="w-10 h-10 rounded-full" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
          <User className="h-5 w-5 text-slate-500" />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-1">
          <span className="font-bold text-[#001f3f]">{comment.user_name}</span>
          <span className="text-xs text-slate-500">
            {new Date(comment.created_at).toLocaleDateString()}
          </span>
        </div>
        <p className="text-slate-700">{comment.content}</p>
      </div>
    </div>
  );
};

export default QBClubPage;
